import moment from 'moment'
import { isEqual, cloneDeep } from 'lodash'
import history from '../../containers/History'

import {
  getDataSource,
  getMe,
  getPrescriptions,
  getPhysiciansAndEkgStackCount,
  getPatients,
  getInterpretations,
  getOverview,
  getActivity,
  getEkgReviewStack,
  postEkgReviewStackRemove,
  postEkgsEmailExport,
  openModal,
  closeModal,
} from '../../store/appReducer'
import {
  DATASOURCES,
  CONFIRMED,
  TRIAGE,
  TRIAGE_OPTION,
  ALL_PATIENTS_OPTION,
  KARDIA_PRO_START_DATE,
  SORT_TYPES,
  EKG_TABLE_COLUMN_HEADERS,
  ALL_PRESCRIPTIONS_OPTION,
  ALL_PHYSICIANS_OPTION,
  MAX_BPM_VALUE,
  MIN_BPM_VALUE,
  EKG_REVIEW_PAGE_TABS,
  ROUTES,
  CONFIRMED_OPTION,
  TABLE_BULK_ACTIONS,
  CLEAR_STATE,
} from '../../constants'
import { sortEkgRecordingIds, filterEkgRecordingIds } from '../../containers/Tables/EkgTable/Utilities'
import { getNextSortDirection, getSelectedList } from '../../containers/Tables/Utilities'
import { handleOnNavClick as handleOnEkgRecordingNavClick } from '../../pages/EkgReviewEkgRecording/module'

// ------------------------------------
// Constants
// ------------------------------------
const PREFIX = 'EkgReview.'
export const UPDATE_BULK_ACTION_IS_FETCHING = `${PREFIX}UPDATE_BULK_ACTION_IS_FETCHING`
export const UPDATE_BULK_ACTION_COMPLETE = `${PREFIX}UPDATE_BULK_ACTION_COMPLETE`
export const UPDATE_BULK_ACTION_SUCCESS_MESSAGE = `${PREFIX}UPDATE_BULK_ACTION_SUCCESS_MESSAGE`
export const UPDATE_REFRESH_CLICKED = `${PREFIX}UPDATE_REFRESH_CLICKED`
export const UPDATE_SELECTED_PAGE_TAB = `${PREFIX}UPDATE_SELECTED_PAGE_TAB`
export const UPDATE_SELECTED_INDEX = `${PREFIX}UPDATE_SELECTED_INDEX`
export const UPDATE_SELECTED_INBOX_ID = `${PREFIX}UPDATE_SELECTED_INBOX_ID`
export const UPDATE_SELECTED_PATIENT_ID = `${PREFIX}UPDATE_SELECTED_PATIENT_ID`
export const UPDATE_SELECTED_PHYSICIAN_ID = `${PREFIX}UPDATE_SELECTED_PHYSICIAN_ID`
export const UPDATE_SELECTED_PHYSICIANS = `${PREFIX}UPDATE_SELECTED_PHYSICIANS`
export const UPDATE_SELECTED_EKG_RESULT = `${PREFIX}UPDATE_SELECTED_EKG_RESULT`
export const UPDATE_SELECTED_DATES = `${PREFIX}UPDATE_SELECTED_DATES`
export const UPDATE_SELECTED_BPM_VALUES = `${PREFIX}UPDATE_SELECTED_BPM_VALUES`
export const UPDATE_SELECTED_CONFIRMED_CURRENT_MONTH = `${PREFIX}UPDATE_SELECTED_CONFIRMED_CURRENT_MONTH`
export const UPDATE_SELECTED_EKGS = `${PREFIX}UPDATE_SELECTED_EKGS`
export const RESET_FILTERS = `${PREFIX}RESET_FILTERS`
export const UPDATE_SORTS = `${PREFIX}UPDATE_SORTS`
export const RESET_SORTS = `${PREFIX}RESET_SORTS`
export const UPDATE_SELECTED_POPOVER = `${PREFIX}UPDATE_SELECTED_POPOVER`
export const DATA_REQUEST = `${PREFIX}DATA_REQUEST`
export const DATA_SUCCESS = `${PREFIX}DATA_SUCCESS`
export const DATA_FAILURE = `${PREFIX}DATA_FAILURE`
export const UPDATE_STACK_NEXT_TOKEN = `${PREFIX}EKG_STACK_NEXT_TOKEN`
export const UPDATE_SELECTED_CUSTOM_COLUMN_ID = `${PREFIX}UPDATE_SELECTED_CUSTOM_COLUMN_ID`

// ------------------------------------
// Actions
// ------------------------------------
export const updateBulkActionIsFetching = flag => ({
  type: UPDATE_BULK_ACTION_IS_FETCHING,
  payload: flag,
})
export const updateBulkActionComplete = flag => ({
  type: UPDATE_BULK_ACTION_COMPLETE,
  payload: flag,
})
export const updateBulkActionSuccessMessage = msg => ({
  type: UPDATE_BULK_ACTION_SUCCESS_MESSAGE,
  payload: msg,
})
export const updateRefreshClicked = clicked => ({
  type: UPDATE_REFRESH_CLICKED,
  payload: clicked,
})
export const updateSelectedPageTab = tab => ({
  type: UPDATE_SELECTED_PAGE_TAB,
  payload: tab,
})

export const updateSelectedIndex = index => ({
  type: UPDATE_SELECTED_INDEX,
  payload: index,
})
export const updateSelectedInboxId = id => ({
  type: UPDATE_SELECTED_INBOX_ID,
  payload: id,
})

export const updateSelectedPatientId = id => ({
  type: UPDATE_SELECTED_PATIENT_ID,
  payload: id,
})
export const updateSelectedCustomColumnId = id => ({
  type: UPDATE_SELECTED_CUSTOM_COLUMN_ID,
  payload: id,
})
export const updateSelectedPhysicianId = id => ({
  type: UPDATE_SELECTED_PHYSICIAN_ID,
  payload: id,
})
export const updateSelectedPhysicians = id => ({
  type: UPDATE_SELECTED_PHYSICIANS,
  payload: id,
})
export const updateSelectedEkgResult = ekgResult => ({
  type: UPDATE_SELECTED_EKG_RESULT,
  payload: ekgResult,
})
export const updateSelectedDates = ({ startDate, endDate }) => ({
  type: UPDATE_SELECTED_DATES,
  payload: {
    startDate,
    endDate,
  },
})
export const updateSelectedBpmValues = ({ startValue, endValue }) => ({
  type: UPDATE_SELECTED_BPM_VALUES,
  payload: {
    startValue,
    endValue,
  },
})
export const updateSelectedConfirmedCurrentMonth = date => ({
  type: UPDATE_SELECTED_CONFIRMED_CURRENT_MONTH,
  payload: date,
})
export const updateSelectedEkgs = selected => ({
  type: UPDATE_SELECTED_EKGS,
  payload: selected,
})

export const resetFilters = () => ({
  type: RESET_FILTERS,
})
export const updateSorts = (sortBy, sortDirection) => ({
  type: UPDATE_SORTS,
  payload: {
    sortBy,
    sortDirection,
  },
})
export const resetSorts = () => ({
  type: RESET_SORTS,
})
export const updateSelectedPopover = popover => ({
  type: UPDATE_SELECTED_POPOVER,
  payload: popover,
})
export const dataRequest = () => ({
  type: DATA_REQUEST,
})
export const dataSuccess = () => ({
  type: DATA_SUCCESS,
})
export const dataFailure = () => ({
  type: DATA_FAILURE,
})
export const updateEKGReviewStackNextToken = (token, params) => ({
  type: UPDATE_STACK_NEXT_TOKEN,
  payload: {
    token,
    params,
  },
})

function shouldGetEkgsData(ekgStackCounts, ekgs, selectedPageTab) {
  if (selectedPageTab !== EKG_REVIEW_PAGE_TABS.TRIAGE) {
    return true
  }
  return !(ekgs && ekgs.data && ekgStackCounts.data && ekgStackCounts.data.triage)
}

export const getEkgReviewData = (forceUpdate = false) => (dispatch, getState) => {
  dispatch(dataRequest())
  const meDataSource = getDataSource(getState(), DATASOURCES.ME)
  const interpretationsDataSource = getDataSource(getState(), DATASOURCES.INTERPRETATIONS)

  let promises

  if (meDataSource.data && !forceUpdate) {
    promises = Promise.resolve()
  } else {
    promises = dispatch(getMe())
  }

  if (interpretationsDataSource.data && !forceUpdate) {
    // no-op
  } else {
    promises = promises
      .then(() => {
        const meDataSource2 = getDataSource(getState(), DATASOURCES.ME)
        const {
          teams,
        } = meDataSource2.data

        return dispatch(getInterpretations(teams[0].id))
      })
  }

  return promises
    .then(() => {
      const me = getDataSource(getState(), DATASOURCES.ME)
      const {
        teams,
      } = me.data

      const {
        selectedPageTab,
        selectedInboxId,
        stackNextResultsToken,
      } = getState().ekgReview

      switch (selectedPageTab) {
      case EKG_REVIEW_PAGE_TABS.PHYSICIAN:
        if (selectedInboxId === undefined || selectedInboxId === TRIAGE_OPTION.id || selectedInboxId === CONFIRMED_OPTION.id) {
          dispatch(updateSelectedInboxId(me.data.profile.id))
        }
        break
      default:
        break
      }

      const physicians = getDataSource(getState(), DATASOURCES.PHYSICIANS)
      const ekgStackCounts = getDataSource(getState(), DATASOURCES.EKG_STACK_COUNTS)
      const patients = getDataSource(getState(), DATASOURCES.PATIENTS)
      const ekgs = getDataSource(getState(), DATASOURCES.EKGS)

      const apiCalls = []
      if (!(ekgs && ekgs.data)) {
        dispatch(updateEKGReviewStackNextToken(null, {}))
        apiCalls.push(dispatch(getEkgStack()))
      }

      if (!physicians.data || !ekgStackCounts.data || forceUpdate) {
        apiCalls.push(dispatch(getPhysiciansAndEkgStackCount(teams[0].id)))
      }

      if (!patients.data || (patients.data && Object.keys(patients.data.byId).length <= 1) || forceUpdate) {
        apiCalls.push(dispatch(getPatients(teams[0].id)))
      }

      return Promise.all(apiCalls)
    })
    .then(() => dispatch(dataSuccess()))
    .catch(err => {
      console.log('getEkgReviewData', err)
      dispatch(dataFailure())
    })
}

export const handleOnRefreshClick = () => (dispatch, getState) => {
  dispatch(updateRefreshClicked(true))
  return dispatch(getEkgReviewData(true))
    .then(() => dispatch(updateRefreshClicked(false)))
}

export const getEkgStack = nextStack => (dispatch, getState) => {
  const {
    selectedInboxId,
    selectedConfirmedCurrentMonth,
    stackNextResultsToken,
  } = getState().ekgReview

  const meDataSource = getDataSource(getState(), DATASOURCES.ME)

  let promises

  if (meDataSource.data) {
    promises = Promise.resolve()
  } else {
    promises = dispatch(getMe())
  }

  return promises
    .then(() => {
      const me = getDataSource(getState(), DATASOURCES.ME)
      const {
        teams,
      } = me.data
      const {
        id: teamId,
      } = teams[0]

      const memberId = selectedInboxId
      let getEkgStackApi

      const confirmedStartDate = moment(selectedConfirmedCurrentMonth).startOf('month').toISOString()
      const confirmedEndDate = moment(selectedConfirmedCurrentMonth).endOf('month').toISOString()

      switch (selectedInboxId) {
      case CONFIRMED:
        getEkgStackApi = dispatch(getEkgReviewStack(teamId, {
          memberId: null,
          confirmed: true,
          startTime: confirmedStartDate,
          endTime: confirmedEndDate,
        }))
        break
      case TRIAGE:
        getEkgStackApi = dispatch(getEkgReviewStack(teamId, {
          memberId: null,
          confirmed: false,
          nextToken: nextStack ? stackNextResultsToken : null,
        }))
        break
      default:
        getEkgStackApi = dispatch(getEkgReviewStack(teamId, { memberId, confirmed: false }))
        break
      }

      return Promise.all([
        getEkgStackApi,
        dispatch(getPhysiciansAndEkgStackCount(teamId)),
      ])
    })
}

let previousRecordingIdsFiltersAndSorts
let cacheRecordingIds = []
export const getRecordingIdsFromDataSource = state => {
  const {
    [DATASOURCES.EKG_STACKS]: ekgStacks,
    [DATASOURCES.EKGS]: ekgs,
  } = state.app.dataSources
  const {
    selectedInboxId,
    selectedPatientId,
    selectedCustomColumnId,
    selectedPhysicianId,
    selectedPhysicians,
    selectedEkgResult,
    selectedStartDate,
    selectedEndDate,
    selectedBpmStartValue,
    selectedBpmEndValue,
    selectedConfirmedCurrentMonth,
    sortBy,
    sortDirection,
    dataIsFetching,
  } = state.ekgReview

  const filtersAndSorts = {
    ekgsIsFetching: ekgs && ekgs.isFetching,
    ekgStacksIsFetching: ekgStacks && ekgStacks[selectedInboxId] && ekgStacks[selectedInboxId].isFetching,
    dataIsFetching,
    selectedInboxId,
    selectedPatientId,
    selectedCustomColumnId,
    selectedPhysicians,
    selectedPhysicianId,
    selectedEkgResult,
    selectedStartDate,
    selectedEndDate,
    selectedBpmStartValue,
    selectedBpmEndValue,
    selectedConfirmedCurrentMonth,
    sortBy,
    sortDirection,
  }

  let recordingIds = []
  let recordingByIds = {}
  const isConfirmedDate = selectedInboxId === CONFIRMED

  if (ekgs && ekgs.data && ekgStacks && ekgStacks.data && ekgStacks.data.stacks[selectedInboxId]) {
    recordingIds = [ ...ekgStacks.data.stacks[selectedInboxId].sortedByTimestamp ]
    recordingByIds = ekgs.data.byId

    if (!isEqual(previousRecordingIdsFiltersAndSorts, filtersAndSorts)) {
      recordingIds = filterEkgRecordingIds(filtersAndSorts, recordingIds, recordingByIds, isConfirmedDate)
      recordingIds = sortEkgRecordingIds(filtersAndSorts, recordingIds, recordingByIds)

      cacheRecordingIds = recordingIds
    }

    previousRecordingIdsFiltersAndSorts = filtersAndSorts
  }

  if (previousRecordingIdsFiltersAndSorts && (previousRecordingIdsFiltersAndSorts.selectedInboxId !== filtersAndSorts.selectedInboxId)) {
    cacheRecordingIds = []
  }
  return cacheRecordingIds
}

export const handleOnSortClick = columnHeader => (dispatch, getState) => {
  const {
    sortBy,
    sortDirection,
  } = getState().ekgReview

  if (sortBy === columnHeader) {
    const nextSortDirection = getNextSortDirection(sortDirection)
    dispatch(updateSorts(sortBy, nextSortDirection))
  } else {
    const nextSortDirection = getNextSortDirection()
    dispatch(updateSorts(columnHeader, nextSortDirection))
  }
}

export const handleOnFilterButtonClick = columnHeader => (dispatch, getState) => {
  const {
    selectedPopover,
  } = getState().ekgReview

  if (selectedPopover === columnHeader) {
    dispatch(updateSelectedPopover(null))
  } else {
    dispatch(updateSelectedPopover(columnHeader))
  }
}

export const handleOnEKGNextClick = () => (dispatch, getState) =>
  dispatch(getEkgStack(true))
    .then(() => dispatch(dataSuccess()))
    .catch(err => dispatch(dataFailure()))

export const handleOnRowClick = ({ index, rowData }) => (dispatch, getState) => {
  const {
    selectedInboxId,
  } = getState().ekgReview

  const recordingIds = getRecordingIdsFromDataSource(getState())

  dispatch(handleOnEkgRecordingNavClick(false, {
    recordingId: rowData.id,
    inboxId: selectedInboxId,
    recordingIds,
  }))
  dispatch(updateSelectedIndex(index))
  dispatch(handleOnSelectionClick({}))
}

export const handleOnPhysicianInboxChange = inboxId => (dispatch, getState) => {
  dispatch(dataRequest())
  dispatch(resetFilters())
  dispatch(resetSorts())
  dispatch(updateSelectedInboxId(inboxId))
  dispatch(updateEKGReviewStackNextToken(null, {}))

  return dispatch(getEkgStack())
    .then(() => dispatch(dataSuccess()))
    .catch(err => dispatch(dataFailure()))
}

export const handleOnFilterChange = (columnHeader, data) => (dispatch, getState) => {
  switch (columnHeader) {
  case EKG_TABLE_COLUMN_HEADERS.RECORDED_AT:
    dispatch(updateSelectedDates(data))
    break
  case EKG_TABLE_COLUMN_HEADERS.PATIENT:
    dispatch(updateSelectedPatientId(data))
    dispatch(updateSelectedPopover(null))
    break
  case EKG_TABLE_COLUMN_HEADERS.ORDERING_PHYSICIAN:
    dispatch(updateSelectedPhysicians(data))
    break
  case EKG_TABLE_COLUMN_HEADERS.ANALYSIS:
    dispatch(updateSelectedEkgResult(data))
    break
  case EKG_TABLE_COLUMN_HEADERS.ANALYSIS_BY:
    dispatch(updateSelectedPhysicianId(data))
    dispatch(updateSelectedPopover(null))
    break
  case EKG_TABLE_COLUMN_HEADERS.BPM:
    dispatch(updateSelectedBpmValues({
      startValue: data[0],
      endValue: data[1],
    }))
    break
  default:
    dispatch(updateSelectedCustomColumnId(data))
    dispatch(updateSelectedPopover(null))
    break
  }
}

export const handleOnSelectionClick = selected => (dispatch, getState) => {
  const ids = getSelectedList(selected)
  dispatch(updateSelectedEkgs(selected))

  if (ids.length === 0) {
    dispatch(updateBulkActionComplete(false))
  }
}

export const handleOnBulkActionTriggerModal = (bulkActionType, data) => (dispatch, getState) => {
  switch (bulkActionType) {
  case TABLE_BULK_ACTIONS.REMOVE_FROM_STACK:
    dispatch(openModal())
    break
  default:
    break
  }
}
export const handleOnBulkActionCloseModal = (bulkActionType, data) => (dispatch, getState) => {
  switch (bulkActionType) {
  case TABLE_BULK_ACTIONS.REMOVE_FROM_STACK:
    dispatch(handleOnSelectionClick({}))
    dispatch(closeModal())
    break
  default:
    break
  }
}

export const handleOnBulkAction = (bulkActionType, data) => (dispatch, getState) => {
  dispatch(dataRequest())

  const {
    selectedEkgs,
    selectedPageTab,
  } = getState().ekgReview

  const selectedIds = getSelectedList(selectedEkgs)

  const meDataSource = getDataSource(getState(), DATASOURCES.ME)

  let successMessage
  let promises

  // ensure we have team id
  if (meDataSource.data) {
    promises = Promise.resolve()
  } else {
    promises = dispatch(getMe())
  }

  switch (bulkActionType) {
  case TABLE_BULK_ACTIONS.MARK_AS_EXPORT:
    promises = promises
      .then(() => {
        const me = getDataSource(getState(), DATASOURCES.ME)

        const {
          teams,
        } = me.data

        successMessage = selectedIds.length === 1
          ? `${selectedIds.length} ECG has been exported. The file will be sent to your email once ready.`
          : `${selectedIds.length} ECGs have been exported. The files will be sent to your email once ready.`

        return dispatch(postEkgsEmailExport(teams[0].id, selectedIds))
      })
    break
  case TABLE_BULK_ACTIONS.REMOVE_FROM_STACK:
    promises = promises
      .then(() => {
        const me = getDataSource(getState(), DATASOURCES.ME)
        let inboxTitle

        const {
          teams,
        } = me.data

        switch (selectedPageTab) {
        case EKG_REVIEW_PAGE_TABS.PHYSICIAN:
          inboxTitle = 'Personal'
          break
        case EKG_REVIEW_PAGE_TABS.TRIAGE:
          inboxTitle = 'Practice'
          break
        default:
          break
        }

        successMessage = selectedIds.length === 1
          ? `${selectedIds.length} ECG has been removed from the ${inboxTitle} inbox.`
          : `${selectedIds.length} ECGs have been removed from the ${inboxTitle} inbox.`

        return dispatch(postEkgReviewStackRemove(teams[0].id, selectedIds))
      })
    break
  default:
    break
  }

  dispatch(updateBulkActionIsFetching(true))
  return promises
    .then(() => {
      window.setTimeout(() => {
        dispatch(handleOnSelectionClick({}))
      }, 3000)
      dispatch(updateBulkActionIsFetching(false))
      dispatch(updateBulkActionSuccessMessage(successMessage))
      dispatch(closeModal())
      return dispatch(updateBulkActionComplete(true))
    })
    // .then(() => dispatch(updateSelectedEkgs({})))
    .then(() => dispatch(dataSuccess()))
    .catch(err => dispatch(dataFailure()))
}

export const handleOnTabClick = tab => (dispatch, getState) => {
  dispatch(dataRequest())
  dispatch(updateEKGReviewStackNextToken(null, {}))

  const meDataSource = getDataSource(getState(), DATASOURCES.ME)

  let promises

  if (meDataSource.data) {
    promises = Promise.resolve()
  } else {
    promises = dispatch(getMe())
  }

  promises = promises
    .then(() => {
      const me = getDataSource(getState(), DATASOURCES.ME)

      dispatch(resetPage(tab, me.data.profile.id))
    })

  return promises
    .then(() => dispatch(getEkgStack()))
    .then(() => dispatch(dataSuccess()))
    .catch(err => dispatch(dataFailure()))
}

export const handleOnNavClick = (resetData = true, handleRouting = false, data = {
  inboxId: undefined,
}) => (dispatch, getState) => {
  const {
    inboxId,
  } = data

  let tab
  switch (inboxId) {
  case CONFIRMED:
    tab = EKG_REVIEW_PAGE_TABS.CONFIRMED
    break
  case TRIAGE:
    tab = EKG_REVIEW_PAGE_TABS.TRIAGE
    break
  default:
    tab = EKG_REVIEW_PAGE_TABS.PHYSICIAN
    break
  }

  let promises = Promise.resolve()
  if (handleRouting) {
    promises = promises
      .then(() => history.push(ROUTES.EKG_REVIEW))
  }

  if (resetData) {
    dispatch(dataRequest())
    dispatch(updateEKGReviewStackNextToken(null, {}))
    promises = promises
      .then(() => dispatch(resetPage(tab, inboxId)))
      .then(() => dispatch(getEkgStack()))
      .then(() => dispatch(dataSuccess()))
      .catch(err => dispatch(dataFailure()))
  }

  return promises
}

const resetPage = (tab, inboxId) => (dispatch, getState) => {
  const physicianId = tab === EKG_REVIEW_PAGE_TABS.PHYSICIAN ? inboxId : undefined

  dispatch(updateSelectedPageTab(tab))
  dispatch(updateSelectedIndex(-1))
  dispatch(updateSelectedPopover(null))
  dispatch(resetFilters())
  dispatch(resetSorts())

  switch (tab) {
  case EKG_REVIEW_PAGE_TABS.TRIAGE:
    dispatch(updateSelectedInboxId(TRIAGE))
    break
  case EKG_REVIEW_PAGE_TABS.PHYSICIAN:
    dispatch(updateSelectedInboxId(physicianId))
    break
  case EKG_REVIEW_PAGE_TABS.CONFIRMED:
    dispatch(updateSelectedInboxId(CONFIRMED))
    dispatch(updateSelectedDatesForConfirmedMonth())
    dispatch(updateSorts(EKG_TABLE_COLUMN_HEADERS.CONFIRMED_AT, sorts.sortDirection))
    break
  default:
    break
  }

  dispatch(updateBulkActionIsFetching(false))
  dispatch(updateBulkActionComplete(false))
  dispatch(updateSelectedEkgs({}))
}

export const handleOnConfirmedMonthClick = (direction = 0) => (dispatch, getState) =>
  Promise.resolve()
    .then(() => {
      const {
        selectedConfirmedCurrentMonth,
      } = getState().ekgReview

      let newMonth = selectedConfirmedCurrentMonth

      if (direction <= -1) {
        newMonth = moment(selectedConfirmedCurrentMonth).subtract(1, 'month').startOf('month').toDate()
      } else if (direction >= 1) {
        newMonth = moment(selectedConfirmedCurrentMonth).add(1, 'month').startOf('month').toDate()
      }

      dispatch(updateSelectedConfirmedCurrentMonth(newMonth))
      dispatch(updateSelectedIndex(-1))
      dispatch(updateSelectedDatesForConfirmedMonth())
      dispatch(updateBulkActionIsFetching(false))
      dispatch(updateBulkActionComplete(false))
      dispatch(updateSelectedEkgs({}))
    })
    .then(() => dispatch(handleOnRefreshClick()))

export const updateSelectedDatesForConfirmedMonth = () => (dispatch, getState) => {
  const me = getDataSource(getState(), DATASOURCES.ME)
  const {
    selectedConfirmedCurrentMonth,
  } = getState().ekgReview
  const {
    id: teamId,
  } = me.data.teams[0]

  const confirmedStartDate = moment(selectedConfirmedCurrentMonth).startOf('month').toDate()
  const confirmedEndDate = moment(selectedConfirmedCurrentMonth).endOf('month').toDate()

  const confirmedStartDateIso = moment(selectedConfirmedCurrentMonth).startOf('month').toISOString()
  const confirmedEndDateIso = moment(selectedConfirmedCurrentMonth).endOf('month').toISOString()

  dispatch(getEkgReviewStack(teamId, {
    memberId: null,
    confirmed: true,
    startTime: confirmedStartDateIso,
    endTime: confirmedEndDateIso,
  }))
  dispatch(updateSelectedDates({ startDate: confirmedStartDate, endDate: confirmedEndDate }))
}


const filters = Object.assign({}, {
  selectedPatientId: ALL_PATIENTS_OPTION.id,
  selectedPhysicianId: ALL_PHYSICIANS_OPTION.id,
  selectedEkgResult: [],
  selectedStartDate: KARDIA_PRO_START_DATE,
  selectedEndDate: moment().endOf('day').toDate(),
  selectedBpmStartValue: MIN_BPM_VALUE,
  selectedBpmEndValue: MAX_BPM_VALUE,
  selectedConfirmedCurrentMonth: moment().startOf('month').toDate(),
  selectedEkgs: {},
  selectedPhysicians: [],
})
const sorts = Object.assign({}, {
  sortBy: EKG_TABLE_COLUMN_HEADERS.RECORDED_AT,
  sortDirection: SORT_TYPES.DESC,
})
const initialState = {
  ...cloneDeep(filters),
  ...cloneDeep(sorts),
  bulkActionIsFetching: false,
  bulkActionComplete: false,
  bulkActionSuccessMessage: null,
  refreshClicked: false,
  selectedIndex: -1,
  selectedInboxId: undefined,
  selectedPageTab: EKG_REVIEW_PAGE_TABS.PHYSICIAN,
  selectedPopover: null,
  selectedCustomColumnId: undefined,
}

// ------------------------------------
// Action Handlers
// ------------------------------------
const ACTION_HANDLERS = {
  [UPDATE_BULK_ACTION_IS_FETCHING]: (state, action) => ({
    ...state, bulkActionIsFetching: action.payload,
  }),
  [UPDATE_BULK_ACTION_COMPLETE]: (state, action) => ({
    ...state, bulkActionComplete: action.payload,
  }),
  [UPDATE_BULK_ACTION_SUCCESS_MESSAGE]: (state, action) => ({
    ...state, bulkActionSuccessMessage: action.payload,
  }),
  [UPDATE_REFRESH_CLICKED]: (state, action) => ({
    ...state, refreshClicked: action.payload,
  }),
  [UPDATE_SELECTED_PAGE_TAB]: (state, action) => ({
    ...state, selectedPageTab: action.payload,
  }),
  [UPDATE_SELECTED_INDEX]: (state, action) => ({
    ...state, selectedIndex: action.payload,
  }),
  [UPDATE_SELECTED_INBOX_ID]: (state, action) => ({
    ...state, selectedInboxId: action.payload,
  }),
  [UPDATE_SELECTED_PATIENT_ID]: (state, action) => ({
    ...state, selectedPatientId: action.payload,
  }),
  [UPDATE_SELECTED_CUSTOM_COLUMN_ID]: (state, action) => ({
    ...state, selectedCustomColumnId: action.payload,
  }),
  [UPDATE_SELECTED_PHYSICIAN_ID]: (state, action) => ({
    ...state, selectedPhysicianId: action.payload,
  }),
  [UPDATE_SELECTED_PHYSICIANS]: (state, action) => ({
    ...state, selectedPhysicians: action.payload,
  }),
  [UPDATE_SELECTED_EKG_RESULT]: (state, action) => ({
    ...state, selectedEkgResult: action.payload,
  }),
  [UPDATE_SELECTED_DATES]: (state, action) => ({
    ...state, selectedStartDate: action.payload.startDate, selectedEndDate: action.payload.endDate,
  }),
  [UPDATE_SELECTED_BPM_VALUES]: (state, action) => ({
    ...state, selectedBpmStartValue: action.payload.startValue, selectedBpmEndValue: action.payload.endValue,
  }),
  [UPDATE_SELECTED_CONFIRMED_CURRENT_MONTH]: (state, action) => ({
    ...state, selectedConfirmedCurrentMonth: action.payload,
  }),
  [UPDATE_SELECTED_EKGS]: (state, action) => ({
    ...state, selectedEkgs: action.payload,
  }),
  [RESET_FILTERS]: (state, action) => ({
    ...state, ...filters,
  }),
  [UPDATE_SORTS]: (state, action) => ({
    ...state, sortBy: action.payload.sortBy, sortDirection: action.payload.sortDirection,
  }),
  [RESET_SORTS]: (state, action) => ({
    ...state, ...sorts,
  }),
  [UPDATE_SELECTED_POPOVER]: (state, action) => ({
    ...state, selectedPopover: action.payload,
  }),
  [DATA_REQUEST]: (state, action) => ({
    ...state, dataIsFetching: true,
  }),
  [DATA_SUCCESS]: (state, action) => ({
    ...state, dataIsFetching: false, dataLastUpdated: new Date(),
  }),
  [DATA_FAILURE]: (state, action) => ({
    ...state, dataIsFetching: false, dataLastUpdated: new Date(),
  }),
  [CLEAR_STATE]: (state, action) => ({ ...initialState }),
  [UPDATE_STACK_NEXT_TOKEN]: (state, action) => ({
    ...state, stackNextResultsToken: action.payload,
  }),
}

// ------------------------------------
// Reducer
// ------------------------------------
export default function ekgReviewReducer(state = initialState, action) {
  const handler = ACTION_HANDLERS[action.type]
  return handler ? handler(state, action) : state
}



// WEBPACK FOOTER //
// ./src/legacy/pages/EkgReview/module.js